/**
 * Pre-Departure Requirements Database
 * Based on actual UK regulations: WBC3, MGN 469, MSN 1823, COP13, SI 1999/2722
 * 
 * This defines what checks are legally required for different vessel types
 * and operating conditions before departure
 */

import { ENGINE_CHECKS, WEATHER_CHECKS, checkTrigger } from './PreDepartureCheckData';
import { CONDITIONAL_CHECKS } from './PreDepartureConditionalChecks';

export const PRE_DEPARTURE_REQUIREMENTS = {
  // UNIVERSAL CHECKS - Required for ALL vessels per WBC3
  universal: [
    {
      id: 'steering_test',
      label: 'Steering gear - test full movement',
      regulation: 'WBC3 Section 10.1', // Seagoing only
      type: 'test',
      required: true,
      description: 'Test steering lock to lock before departure',
      category: 'machinery'
    },
    {
      id: 'nav_lights',
      label: 'Navigation lights - visual check all sectors',
      regulation: 'WBC3 Section 18.1',
      type: 'check',
      required: true,
      description: 'Check all navigation lights working',
      category: 'navigation'
    },
    {
      id: 'nav_equipment',
      label: 'Navigation equipment - test operation',
      regulation: 'WBC3 Section 19', // Updated to WBC3 for seagoing
      type: 'test',
      required: true,
      description: 'Test all navigation equipment (GPS, compass, etc)',
      category: 'navigation'
    },
    {
      id: 'vhf_radio_test',
      label: 'VHF radio - test operation',
      regulation: 'WBC3 Section 17.1',
      type: 'test',
      required: true,
      description: 'Test VHF radio operation',
      category: 'communications'
    },
    {
      id: 'engine_test',
      label: 'Engine tested ahead/astern',
      regulation: 'WBC3 Section 10', // Engine test requirement
      type: 'test',
      required: true,
      description: 'Test engine operation in both directions',
      category: 'machinery'
    },
    {
      id: 'safety_equipment',
      label: 'Safety equipment - verify present and accessible',
      regulation: 'WBC3 Section 14', // Safety equipment section
      type: 'check',
      required: true,
      description: 'Check all safety gear present and accessible',
      category: 'safety'
    },
    {
      id: 'stern_gland',
      label: 'Stern gland - check for leaks',
      regulation: 'WBC3 Section 8.3',
      type: 'check',
      required: true,
      description: 'Check stern gland, adjust if necessary',
      category: 'machinery'
    }
    // Note: Crew certificates validation is handled automatically by the system
    // when crew are assigned to a voyage - not a manual pre-departure check
  ],

  // CONDITIONAL CHECKS - Imported from PreDepartureConditionalChecks
  conditional: CONDITIONAL_CHECKS,

  // ENGINE CHECKS - Imported from PreDepartureCheckData
  engine: ENGINE_CHECKS,

  // WEATHER OBSERVATIONS - Imported from PreDepartureCheckData
  weather: WEATHER_CHECKS
};

/**
 * Get required checks for a specific vessel configuration
 */
export function getRequiredChecks(vesselConfig) {
  const checks = [];
  
  // Always include universal checks (actually required by WBC3 for ALL vessels)
  checks.push(...PRE_DEPARTURE_REQUIREMENTS.universal);
  
  // Engine test is now in universal checks
  // Weather forecast is handled in conditional checks for Cat 0-4
  
  // Check each conditional requirement
  Object.values(PRE_DEPARTURE_REQUIREMENTS.conditional).forEach(requirement => {
    if (checkTrigger(requirement.trigger, vesselConfig)) {
      checks.push(...requirement.checks);
    }
  });
  
  // Remove duplicates
  const uniqueChecks = [];
  const ids = new Set();
  checks.forEach(check => {
    if (!ids.has(check.id)) {
      ids.add(check.id);
      uniqueChecks.push(check);
    }
  });
  
  return uniqueChecks;
}

export default PRE_DEPARTURE_REQUIREMENTS;